import React, { useCallback, useEffect, useState } from "react";
import * as Scrivito from "scrivito";
import NiceModal from "@ebay/nice-modal-react";
import { Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useMutation, useQuery } from "urql";
import { useTranslate } from "hooks";
import { ToggleCommissioningCostNodeMut } from "api/mutations";
import { GetCommissioningCostNodeQry } from "api/queries";
import { Button } from "../Button";
import { Spinner } from "../Spinner";
import { Switcher } from "components/Switcher/Switcher";
import { useProjectActions } from "stores/project/project/projectStore";

type Props = {
  nodeId: string;
};

export const CommissioningCostModal = NiceModal.create(
  Scrivito.connect(({ nodeId }: Props): React.ReactElement => {
    const translate = useTranslate();
    const modal = NiceModal.useModal();
    const projectActions = useProjectActions();

    const [commissioningEnabled, setCommissioningEnabled] = useState(false);

    const [{ data, fetching: queryFetching }] = useQuery({
      query: GetCommissioningCostNodeQry,
      variables: { input: { wizardRootNodeID: nodeId } },
      requestPolicy: "network-only",
      pause: !modal.visible,
    });

    useEffect(() => {
      if (data) {
        setCommissioningEnabled(data.getCommissioningCostNode?.active ?? false);
      }
    }, [data]);

    const [toggleResult, executeToggle] = useMutation(
      ToggleCommissioningCostNodeMut,
    );
    const isSaving = toggleResult.fetching;

    const handleToggle = useCallback((checked: boolean) => {
      setCommissioningEnabled(checked);
    }, []);

    const handleSave = useCallback(async () => {
      if (isSaving) return;

      const { error } = await executeToggle({
        input: { wizardRootNodeID: nodeId, active: commissioningEnabled },
      });

      if (!error) {
        projectActions.refetch();
        modal.resolve();
        modal.remove();
      }
    }, [
      isSaving,
      commissioningEnabled,
      executeToggle,
      nodeId,
      modal,
      projectActions,
    ]);

    return (
      <Modal
        isOpen={modal.visible}
        centered={true}
        size="md"
        toggle={modal.hide}
      >
        <ModalHeader
          toggle={modal.hide}
          tag="div"
          className="d-flex align-items-start pb-3"
        >
          <h3 className="text-uppercase mb-1">
            {translate("ADD_SERVICES_MODAL_TITLE")}
          </h3>
          <h4 className="mb-0">{translate("ADD_SERVICES_MODAL_SUBTITLE")}</h4>
        </ModalHeader>
        <ModalBody className="px-4 py-3">
          {queryFetching ? (
            <div className="d-flex justify-content-center py-3">
              <Spinner size="sm" />
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-between">
              <Label
                className="mb-0 fw-bold fs-6"
                htmlFor="commissioning-switch"
              >
                {translate("WIZARD_COMMISSIONING")}
              </Label>
              <Switcher
                id="commissioning-switch"
                checked={commissioningEnabled}
                onChange={handleToggle}
                disabled={isSaving}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter className="pt-0">
          <Button
            data-testid="CommissioningCostModal_save-btn"
            color="primary"
            disabled={isSaving || queryFetching}
            onClick={handleSave}
          >
            {translate("TEXT_SAVE_CHANGES")}
            {(isSaving || queryFetching) && (
              <Spinner size="xxs" className="ms-2" />
            )}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }),
);
